import { readMeta } from './page';
import { hasUnexpiredJwt, jwtData } from './jwt';

/** Must not be `_sift` — that id would shadow Sift's `window._sift` command queue. */
export const SIFT_SCRIPT_ELEMENT_ID = 'sift-science-script';

declare global {
  interface Window {
    _sift?: Array<unknown>;
  }
}

function siftQueue(): Array<unknown> {
  if (!Array.isArray(window._sift)) {
    window._sift = [];
  }
  return window._sift;
}

let configuredIdentity: { ssoUuid: string; sessionId: string | null } | null = null;

export function resetSiftTrackingState(): void {
  configuredIdentity = null;
}

function identityMatchesConfigured(ssoUuid: string, sessionId: string | null): boolean {
  if (!configuredIdentity) return false;

  return configuredIdentity.ssoUuid === ssoUuid && configuredIdentity.sessionId === sessionId;
}

function siftIdentity(): { ssoUuid: string | null; sessionId: string | null } {
  if (!hasUnexpiredJwt()) {
    return { ssoUuid: null, sessionId: null };
  }

  const jwt = jwtData();
  const ssoUuid = typeof jwt.sub === 'string' ? jwt.sub : null;
  const sessionId = typeof jwt.session_id === 'string' ? jwt.session_id : null;

  return { ssoUuid, sessionId };
}

export function shouldTrackSift(): boolean {
  const siftKey = readMeta('sift');
  if (!siftKey) return false;

  const { ssoUuid } = siftIdentity();
  return Boolean(ssoUuid);
}

export function injectSiftScript(): void {
  if (document.querySelector(`#${SIFT_SCRIPT_ELEMENT_ID}`)) return;

  const script = document.createElement('script');
  script.src = 'https://cdn.siftscience.com/s.js';
  script.id = SIFT_SCRIPT_ELEMENT_ID;
  document.body.appendChild(script);
}

/**
 * Queue account, user, and session whenever the logged-in identity changes.
 * Returns whether the current visitor is eligible for Sift tracking.
 */
function configureSiftAccount(): boolean {
  const siftKey = readMeta('sift');
  const { ssoUuid, sessionId } = siftIdentity();
  if (!siftKey || !ssoUuid) return false;

  if (!identityMatchesConfigured(ssoUuid, sessionId)) {
    const sift = siftQueue();

    sift.push(['_setAccount', siftKey]);
    sift.push(['_setUserId', ssoUuid]);
    sift.push(['_setSessionId', sessionId]);
    configuredIdentity = { ssoUuid, sessionId };
  }

  return true;
}

export function trackSiftPageview(): void {
  if (!configureSiftAccount()) {
    resetSiftTrackingState();
    return;
  }

  siftQueue().push(['_trackPageview']);
}

/** Start Sift after client-side login when the JWT cookie is set without a full reload. */
export function startSiftTracking(): void {
  if (!shouldTrackSift()) return;

  injectSiftScript();
  configureSiftAccount();
}
